import React, { useState, useEffect, useRef } from 'react';
import { ProgressBar, Button, Alert } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { updateGuestyReservation, parseApiResponse } from './webhookService';

export default function SyncProgressBar({ 
  totalCount, 
  reservationIds, 
  onComplete, 
  onCancel, 
  clientId 
}) {
  const [progress, setProgress] = useState(0);
  const [isSyncing, setIsSyncing] = useState(true);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [error, setError] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cancelRef = useRef(false);
  
  useEffect(() => {
    let timeoutId;
    let completed = 0;
    let failed = 0;
    
    const syncNextReservation = async () => {
      if (cancelRef.current || completed + failed >= totalCount || completed + failed >= reservationIds.length) {
        if (cancelRef.current) {
          onCancel();
        } else {
          onComplete();
        }
        setIsSyncing(false);
        return;
      }
      
      const currentReservationId = reservationIds[completed + failed];
      if (!currentReservationId) {
        setIsSyncing(false);
        onComplete();
        return;
      }
      
      try {
        const response = await updateGuestyReservation(currentReservationId);
        const result = await parseApiResponse(response);
        
        if (result.success) {
          setCurrentReservation(result.data);
          
          completed++;
          const newProgress = Math.round(((completed + failed) / totalCount) * 100);
          setProgress(newProgress);
        } else {
          // Log the error but continue with the next reservation
          console.error(`Failed to update reservation: ${result.error}`);
          setError(`Failed to update reservation: ${result.error}`);
          failed++;
          setFailedCount(failed);
          
          const newProgress = Math.round(((completed + failed) / totalCount) * 100);
          setProgress(newProgress);
        }
        
        // Schedule next update after 1 second regardless of success/failure
        timeoutId = setTimeout(syncNextReservation, 1000);
      } catch (error) {
        console.error('Error updating reservation:', error);
        setError('An error occurred while updating reservations');
        
        // Continue with the next reservation despite the error
        failed++;
        setFailedCount(failed);
        
        const newProgress = Math.round(((completed + failed) / totalCount) * 100);
        setProgress(newProgress);
        
        // Schedule next update after 1 second
        timeoutId = setTimeout(syncNextReservation, 1000);
      }
    };
    
    // Start the sync process
    syncNextReservation();
    
    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [totalCount, onComplete, onCancel, clientId]);
  
  const handleCancel = () => {
    cancelRef.current = true;
  };
  
  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="small">
          {isSyncing ? (
            <span>Syncing reservations... ({progress}%)</span>
          ) : (
            <span>
              {failedCount > 0 ? `Sync completed with ${failedCount} errors` : 'Sync completed'}
            </span>
          )}
        </div>
        {isSyncing && (
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={handleCancel}
            className="py-0 px-2"
          >
            <FaTimes /> Cancel
          </Button>
        )}
      </div>
      
      <ProgressBar 
        now={progress} 
        variant={error ? "danger" : "success"} 
        animated={isSyncing}
      />
      
      {currentReservation && (
        <div className="mt-2 small text-muted">
          Last updated: {currentReservation.listingAddress}
        </div>
      )}
      
      {error && (
        <Alert variant="warning" className="mt-2 py-2 small">
          Last error: {error}
          {failedCount > 0 && <div>Failed updates: {failedCount}</div>}
        </Alert>
      )}
    </div>
  );
}
